import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import { siteConfig } from "../config/siteConfig";
import heroImg from "../assets/images/hero_raw_wool.jpg";
import warehouseImg from "../assets/images/wool_bales_warehouse.jpg";
import {
  ShieldCheck,
  Award,
  Layers,
  Globe,
  ArrowRight,
  Sliders,
  CheckCircle2,
  Phone,
  MessageCircle,
  Building2,
  Compass,
  Factory,
  PackageCheck,
  TrendingUp,
  HelpCircle,
} from "lucide-react";

const Home = () => {
  const { t, lPath, isRTL } = useLanguage();

  const whatWeDoCards = [
    {
      icon: <Layers className="w-6 h-6 text-wool-bronze" />,
      title: t("whatWeDo.cards.sourcing.title"),
      desc: t("whatWeDo.cards.sourcing.description"),
    },
    {
      icon: <PackageCheck className="w-6 h-6 text-wool-bronze" />,
      title: t("whatWeDo.cards.supply.title"),
      desc: t("whatWeDo.cards.supply.description"),
    },
    {
      icon: <Sliders className="w-6 h-6 text-wool-bronze" />,
      title: t("whatWeDo.cards.procurement.title"),
      desc: t("whatWeDo.cards.procurement.description"),
    },
    {
      icon: <Globe className="w-6 h-6 text-wool-bronze" />,
      title: t("whatWeDo.cards.international.title"),
      desc: t("whatWeDo.cards.international.description"),
    },
    {
      icon: <Building2 className="w-6 h-6 text-wool-bronze" />,
      title: t("whatWeDo.cards.relationships.title"),
      desc: t("whatWeDo.cards.relationships.description"),
    },
  ];

  const specCards = [
    { key: "micron", title: t("specs.items.micron.title"), desc: t("specs.items.micron.desc"), tag: "30+ / 32–34+ Micron Focus" },
    { key: "stapleLength", title: t("specs.items.stapleLength.title"), desc: t("specs.items.stapleLength.desc"), tag: "50mm – 100mm+" },
    { key: "yield", title: t("specs.items.yield.title"), desc: t("specs.items.yield.desc"), tag: "Clean Yield Testing" },
    { key: "strength", title: t("specs.items.strength.title"), desc: t("specs.items.strength.desc"), tag: "High Tensile Crimp" },
    { key: "colour", title: t("specs.items.colour.title"), desc: t("specs.items.colour.desc"), tag: "Natural & White Grades" },
    { key: "vm", title: t("specs.items.vm.title"), desc: t("specs.items.vm.desc"), tag: "Low Vegetable Matter" },
    { key: "condition", title: t("specs.items.condition.title"), desc: t("specs.items.condition.desc"), tag: "Greasy & Scoured" },
    { key: "origin", title: t("specs.items.origin.title"), desc: t("specs.items.origin.desc"), tag: "Bikaner & Global Origins" },
  ];

  const whyChooseCards = [
    {
      title: t("whyChooseUs.cards.legacy.title"),
      desc: t("whyChooseUs.cards.legacy.desc"),
      icon: <Award className="w-5 h-5 text-wool-bronze" />,
    },
    {
      title: t("whyChooseUs.cards.bikaner.title"),
      desc: t("whyChooseUs.cards.bikaner.desc"),
      icon: <Compass className="w-5 h-5 text-wool-bronze" />,
    },
    {
      title: t("whyChooseUs.cards.relationships.title"),
      desc: t("whyChooseUs.cards.relationships.desc"),
      icon: <Building2 className="w-5 h-5 text-wool-bronze" />,
    },
    {
      title: t("whyChooseUs.cards.specBased.title"),
      desc: t("whyChooseUs.cards.specBased.desc"),
      icon: <Sliders className="w-5 h-5 text-wool-bronze" />,
    },
    {
      title: t("whyChooseUs.cards.bulk.title"),
      desc: t("whyChooseUs.cards.bulk.desc"),
      icon: <Factory className="w-5 h-5 text-wool-bronze" />,
    },
    {
      title: t("whyChooseUs.cards.longTerm.title"),
      desc: t("whyChooseUs.cards.longTerm.desc"),
      icon: <TrendingUp className="w-5 h-5 text-wool-bronze" />,
    },
  ];

  const faqs = [
    {
      q: "What types of raw wool does S.K. Woollens trade and supply?",
      a: "S.K. Woollens specializes in indigenous Indian carpet-grade raw wool, primarily in 30+ micron and 32–34+ micron coarse fleece. We supply in greasy (raw unwashed farm lots) and scoured commercial bales, suited for spinning mills, carpet yarn manufacturers, and industrial wool buyers."
    },
    {
      q: "Where is S.K. Woollens physically based, and how are lots procured?",
      a: "Our central trading desk and aggregation godowns are located at B-26, Un Mandi, Antyodaya Nagar, Bikaner, Rajasthan 334001. Operating directly within Asia's largest wool market, our team samples, inspects, and consolidates fleece arrivals from pastoralist shearing tracts across Bikaner and greater Rajasthan."
    },
    {
      q: "Which types of business buyers does S.K. Woollens serve?",
      a: "We serve B2B commercial entities including carpet yarn spinning mills, woollen textile manufacturers, hand-knotted and tufted carpet manufacturers, commission buying agents, and domestic/international trading partners."
    },
    {
      q: "What is the commercial enquiry and order process?",
      a: "Buyers contact our desk with their technical specifications (target micron, staple length, clean yield, vegetable matter limits, and tonnage). We provide spot lot samples, certified weighment details, commercial price quotes, and coordinate direct truckload dispatch to mill facilities across India."
    },
    {
      q: "How can buyers connect directly with the management?",
      a: "You can reach partners Nikhil Banthia (+91 7014581250) and Sunil Banthia (+91 9829109294) directly via phone or WhatsApp. Our trading desk operates Monday through Saturday from B-26 Un Mandi, Bikaner."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  return (
    <div className="space-y-20 sm:space-y-28">
      <SEO
        title="S.K. Woollens | Raw Wool & Carpet-Grade Wool Trading in Bikaner"
        description="Connect with S.K. Woollens for B2B raw wool and carpet-grade wool trading in Bikaner, Rajasthan. Explore our wool supply and sourcing capabilities for carpet and yarn industries."
        keywords="wool supplier in Bikaner, raw wool supplier in Bikaner, wool trader in Bikaner, wool agent in Bikaner, carpet grade wool supplier in Bikaner, bulk wool supplier Rajasthan, carpet wool supplier India, S.K. Woollens Bikaner"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en"
        schema={[faqSchema]}
      />
      {/* 1. HERO SECTION */}
      <section className="relative pt-8 pb-12 sm:pt-14 sm:pb-20 overflow-hidden bg-gradient-to-b from-wool-sand/40 via-wool-bg to-wool-bg border-b border-wool-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Text Column */}
            <div className="lg:col-span-7 space-y-6 text-start">
              {/* Trust Badge */}
              <div className="wool-badge inline-flex">
                <ShieldCheck className="w-3.5 h-3.5 text-wool-bronze" />
                <span>{t("hero.badge")}</span>
              </div>

              {/* Headlines */}
              <div className="space-y-3">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight leading-[1.15] font-display">
                  {t("hero.headline")}
                </h1>
                <p className="text-lg sm:text-xl font-semibold text-wool-bronze leading-snug">
                  {t("hero.subheadline")}
                </p>
              </div>

              {/* Supporting Text */}
              <p className="text-sm sm:text-base text-wool-slate leading-relaxed max-w-2xl">
                {t("hero.description")}
              </p>

              {/* Heritage Note */}
              <div className="text-xs text-wool-muted bg-white/70 border border-wool-border/80 px-4 py-2.5 rounded-xl inline-block max-w-xl">
                <span className="font-semibold text-wool-bronze-dark">1965 Legacy: </span>
                {t("hero.legacyNote")}
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <span>{t("hero.primaryCta")}</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <Link to={lPath("/contact")} className="btn-secondary">
                  {t("hero.secondaryCta")}
                </Link>

                <Link
                  to={lPath("/for-international-suppliers")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-wool-bronze hover:text-wool-bronze-dark px-3 py-2 transition"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{t("hero.supplierCta")}</span>
                </Link>
              </div>

              {/* Micro Trust Indicators */}
              <div className="pt-4 grid grid-cols-3 gap-4 border-t border-wool-border/60 text-xs">
                <div>
                  <span className="font-bold text-wool-charcoal text-base block">1965</span>
                  <span className="text-wool-muted text-[11px]">Established Heritage</span>
                </div>
                <div>
                  <span className="font-bold text-wool-charcoal text-base block">30+ Micron</span>
                  <span className="text-wool-muted text-[11px]">Carpet Yarn Speciality</span>
                </div>
                <div>
                  <span className="font-bold text-wool-charcoal text-base block">B2B Bulk</span>
                  <span className="text-wool-muted text-[11px]">Mill Supply Reliability</span>
                </div>
              </div>
            </div>

            {/* Visual Column */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-wool-border bg-white group">
                <img
                  src={heroImg}
                  alt="Raw Natural Wool Fleece Fibres"
                  className="w-full h-80 sm:h-[420px] object-cover transition duration-700 group-hover:scale-105"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 inset-x-4 p-4 bg-white/90 backdrop-blur-md rounded-xl border border-white/40 text-wool-charcoal">
                  <div className="text-xs font-bold uppercase tracking-wider text-wool-bronze">
                    Raw Wool Procurement & Supply
                  </div>
                  <div className="text-xs text-wool-slate mt-0.5">
                    Graded for Micron, Length, Clean Yield & Crimp Strength
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT US & 1965 LEGACY PREVIEW */}
      <section id="infrastructure" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-28">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge">
                <Award className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Our Heritage</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal tracking-tight font-display">
                {t("about.title")}
              </h2>
              <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
                {t("about.p1")}
              </p>
              <p className="text-sm text-wool-muted leading-relaxed">
                {t("about.p2")}
              </p>
              <div className="pt-2">
                <Link to={lPath("/about")} className="btn-secondary text-xs">
                  <span>Explore Full Legacy & Timeline</span>
                  <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative rounded-2xl overflow-hidden border border-wool-border shadow-md">
              <img
                src={warehouseImg}
                alt="S.K. Woollens Trading & Warehouse Operations"
                className="w-full h-64 sm:h-72 object-cover"
              />
              <div className="p-3 bg-wool-sand/60 border-t border-wool-border text-center text-xs font-medium text-wool-charcoal">
                Bikaner Wool Market Presence & Industrial B2B Bulk Sourcing
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHAT WE DO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
          <div className="wool-badge">
            <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Core Capabilities</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-wool-charcoal font-display">
            {t("whatWeDo.title")}
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            {t("whatWeDo.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {whatWeDoCards.map((card, idx) => (
            <div
              key={idx}
              className={`wool-card p-6 flex flex-col justify-between ${idx === 4 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-wool-charcoal">
                  {card.title}
                </h3>
                <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                  {card.desc}
                </p>
              </div>
              <div className="pt-4 mt-4 border-t border-wool-border/50 flex items-center gap-1.5 text-xs font-semibold text-wool-bronze">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>B2B Commercial Grade</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3B. SPECIALIZED SOURCING HUBS & SERVICES (B2B Landing Hub) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-sand/30 border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="wool-badge">
                <Building2 className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Specialized Sourcing Hubs</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-wool-charcoal font-display mt-2">
                Explore Dedicated Wool Sourcing & Supply Solutions
              </h2>
            </div>
            <Link to={lPath("/contact")} className="btn-secondary text-xs">
              <span>Contact Sourcing Desk</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-2">
            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="bg-white border border-wool-border rounded-2xl p-4 hover:border-wool-bronze hover:shadow-md transition flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] font-bold text-wool-bronze uppercase">Local Hub</span>
                <h3 className="text-sm font-bold text-wool-charcoal group-hover:text-wool-bronze transition mt-1">
                  Wool Supplier in Bikaner
                </h3>
                <p className="text-xs text-wool-muted mt-1 leading-relaxed">
                  Direct Un Mandi B-26 presence, 60-year trade legacy, and local grading.
                </p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View Bikaner Hub <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </span>
            </Link>

            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="bg-white border border-wool-border rounded-2xl p-4 hover:border-wool-bronze hover:shadow-md transition flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] font-bold text-wool-bronze uppercase">Carpet Grade</span>
                <h3 className="text-sm font-bold text-wool-charcoal group-hover:text-wool-bronze transition mt-1">
                  Carpet Wool Supplier
                </h3>
                <p className="text-xs text-wool-muted mt-1 leading-relaxed">
                  30+ & 32–34+ micron coarse fleece for Bhadohi, Panipat & Mirzapur mills.
                </p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View Carpet Wool <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </span>
            </Link>

            <Link
              to={lPath("/raw-wool-supplier-india")}
              className="bg-white border border-wool-border rounded-2xl p-4 hover:border-wool-bronze hover:shadow-md transition flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] font-bold text-wool-bronze uppercase">Pan-India</span>
                <h3 className="text-sm font-bold text-wool-charcoal group-hover:text-wool-bronze transition mt-1">
                  Raw Wool Supplier India
                </h3>
                <p className="text-xs text-wool-muted mt-1 leading-relaxed">
                  Domestic wool belts, greasy pressed bales, and nationwide truck delivery.
                </p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View India Supply <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-agent-bikaner")}
              className="bg-white border border-wool-border rounded-2xl p-4 hover:border-wool-bronze hover:shadow-md transition flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] font-bold text-wool-bronze uppercase">Commission Agency</span>
                <h3 className="text-sm font-bold text-wool-charcoal group-hover:text-wool-bronze transition mt-1">
                  Wool Agent in Bikaner
                </h3>
                <p className="text-xs text-wool-muted mt-1 leading-relaxed">
                  On-the-ground mandi representation, certified weighing & lot sampling.
                </p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View Mandi Agency <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="bg-white border border-wool-border rounded-2xl p-4 hover:border-wool-bronze hover:shadow-md transition flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] font-bold text-wool-bronze uppercase">Mill Contracts</span>
                <h3 className="text-sm font-bold text-wool-charcoal group-hover:text-wool-bronze transition mt-1">
                  Bulk Wool Supply
                </h3>
                <p className="text-xs text-wool-muted mt-1 leading-relaxed">
                  Multi-tonne monthly contracts, buffer godown storage & dispatch cadence.
                </p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                View Bulk Supply <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. WOOL SPECIFICATIONS SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-white via-wool-sand/20 to-wool-bg border border-wool-border rounded-3xl p-6 sm:p-12 shadow-subtle space-y-10">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Technical Precision</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-wool-charcoal font-display">
              {t("specs.title")}
            </h2>
            <p className="text-sm sm:text-base text-wool-slate">
              {t("specs.subtitle")}
            </p>

            {/* Carpet Micron Highlight Box */}
            <div className="p-4 bg-wool-sand/60 border-s-4 border-wool-bronze rounded-e-xl text-xs sm:text-sm font-semibold text-wool-charcoal mt-4 leading-relaxed">
              {t("specs.carpetHighlight")}
            </div>
          </div>

          {/* Specification Parameter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {specCards.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-wool-border rounded-2xl p-5 hover:border-wool-bronze/50 transition shadow-sm space-y-2"
              >
                <div className="text-[11px] font-bold text-wool-bronze uppercase tracking-wider">
                  {item.tag}
                </div>
                <h4 className="text-sm font-bold text-wool-charcoal">
                  {item.title}
                </h4>
                <p className="text-xs text-wool-muted leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Disclaimer & CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-wool-border text-xs text-wool-muted">
            <p className="max-w-xl italic">
              {t("specs.disclaimer")}
            </p>
            <Link to={lPath("/wool-specifications")} className="btn-primary text-xs shrink-0">
              <span>View Full Specification Matrix</span>
              <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. INTERNATIONAL SOURCING PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Globe className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Global Sourcing Strategy</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-wool-charcoal font-display">
            {t("international.title")}
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            {t("international.statement")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {siteConfig.internationalMarkets.map((m, idx) => (
            <div
              key={idx}
              className="wool-card p-5 space-y-3 bg-white flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold px-2 py-0.5 bg-wool-sand/60 text-wool-bronze-dark rounded">
                    {m.code}
                  </span>
                  <Globe className="w-4 h-4 text-wool-muted" />
                </div>
                <h3 className="text-base font-bold text-wool-charcoal">
                  {m.country}
                </h3>
                <p className="text-xs text-wool-slate leading-relaxed">
                  {m.description}
                </p>
              </div>
              <div className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2.5 py-1 rounded-lg">
                {m.status}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to={lPath("/for-international-suppliers")}
            className="btn-primary text-xs px-6 py-2.5"
          >
            <span>{t("international.partnerCta")}</span>
            <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
          </Link>
        </div>
      </section>

      {/* 6. B2B APPLICATION FLOW (Serving Wool & Yarn Manufacturers) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl space-y-10">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <Factory className="w-3.5 h-3.5 text-wool-sand" />
              <span>B2B Industrial Supply Flow</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              {t("customerFlow.title")}
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              {t("customerFlow.description")}
            </p>
          </div>

          {/* Flow Diagram */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative">
            <div className="p-5 bg-white/5 border border-white/10 rounded-2xl space-y-2">
              <div className="text-xs font-bold text-wool-sand uppercase tracking-wider">Step 1</div>
              <h4 className="text-base font-bold text-white">{t("customerFlow.flowStep1")}</h4>
              <p className="text-xs text-wool-sand/70">{t("customerFlow.flowStep1Sub")}</p>
            </div>

            <div className="p-5 bg-wool-bronze/40 border border-wool-bronze rounded-2xl space-y-2 relative">
              <div className="text-xs font-bold text-wool-sand uppercase tracking-wider">Step 2 (Core)</div>
              <h4 className="text-base font-bold text-white">{t("customerFlow.flowStep2")}</h4>
              <p className="text-xs text-wool-sand/80">{t("customerFlow.flowStep2Sub")}</p>
            </div>

            <div className="p-5 bg-white/5 border border-white/10 rounded-2xl space-y-2">
              <div className="text-xs font-bold text-wool-sand uppercase tracking-wider">Step 3</div>
              <h4 className="text-base font-bold text-white">{t("customerFlow.flowStep3")}</h4>
              <p className="text-xs text-wool-sand/70">{t("customerFlow.flowStep3Sub")}</p>
            </div>

            <div className="p-5 bg-white/5 border border-white/10 rounded-2xl space-y-2">
              <div className="text-xs font-bold text-wool-sand uppercase tracking-wider">Step 4</div>
              <h4 className="text-base font-bold text-white">{t("customerFlow.flowStep4")}</h4>
              <p className="text-xs text-wool-sand/70">{t("customerFlow.flowStep4Sub")}</p>
            </div>
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-white/10">
            <div className="text-xs text-wool-sand/70">
              Looking for dependable raw wool supply for your spinning mill?
            </div>
            <Link
              to={lPath("/for-yarn-manufacturers")}
              className="btn-primary bg-wool-sand text-wool-charcoal hover:bg-white text-xs"
            >
              <span>Explore Yarn Manufacturer Solutions</span>
              <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. WHY CHOOSE S.K. WOOLLENS (6 Cards) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
          <div className="wool-badge">
            <Award className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Proven Reliability</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-wool-charcoal font-display">
            {t("whyChooseUs.title")}
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            {t("whyChooseUs.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyChooseCards.map((item, idx) => (
            <div key={idx} className="wool-card p-6 space-y-3 bg-white">
              <div className="w-10 h-10 rounded-xl bg-wool-sand/60 flex items-center justify-center border border-wool-border">
                {item.icon}
              </div>
              <h3 className="text-lg font-bold text-wool-charcoal">
                {item.title}
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 8. FREQUENTLY ASKED QUESTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          <div className="space-y-2 text-start">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-wool-bronze">
              <HelpCircle className="w-4 h-4" />
              <span>B2B Wool Supply Questions</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-base text-wool-slate max-w-3xl">
              Commercial guidance for spinning mills, carpet manufacturers, and wool procurement managers seeking reliable raw wool sourcing from Bikaner, Rajasthan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white border border-wool-border rounded-2xl p-6 space-y-2.5 shadow-subtle hover:border-wool-bronze/40 transition"
              >
                <h3 className="text-base font-bold text-wool-charcoal flex items-start gap-2">
                  <span className="text-wool-bronze font-bold text-sm shrink-0 mt-0.5">Q.</span>
                  <span>{faq.q}</span>
                </h3>
                <p className="text-xs sm:text-sm text-wool-slate leading-relaxed pl-5">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CALL TO ACTION STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Ready to Discuss Your Raw Wool Requirements?
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Contact our partners directly or submit your technical specifications for prompt quotation and availability.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <span>Submit Wool Requirement</span>
              <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
            </a>

            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              <span>WhatsApp Desk</span>
            </a>

            <a
              href="tel:+917014581250"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call +91 7014581250</span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
